// src/feature-module/crm/deals/components/KanbanColumn.tsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Stage } from '../../../../types/funnels';
import { Opportunity } from '../OpportunityFormHandler';
import TooltipTitle from './TooltipTitle';

interface EtapaLazyData {
  opportunities: Opportunity[];
  loading: boolean;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
  total: number;
  initialized: boolean;
  error: string | null;
}

interface KanbanColumnProps {
  stage: Stage;
  etapaData: EtapaLazyData;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderOpportunityCard: (opportunity: Opportunity) => React.ReactNode;
  containerRef: (el: HTMLDivElement | null) => void;
  stageIndex: number;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  stage,
  etapaData,
  onLoadMore,
  onRefresh,
  renderOpportunityCard,
  containerRef,
  stageIndex
}) => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const loadMoreTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTime = useRef<number>(0);

  // Função para detectar quando o usuário está próximo do final da lista
  const handleScroll = useCallback(() => {
    if (!scrollElement || !etapaData.hasMore || etapaData.loading) return;

    const now = Date.now();
    lastScrollTime.current = now;

    // Usar requestAnimationFrame para melhor performance
    requestAnimationFrame(() => {
      // Verificar se este é ainda o scroll mais recente
      if (lastScrollTime.current !== now) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const scrolledPercentage = (scrollTop + clientHeight) / scrollHeight;
      
      const nearBottom = scrolledPercentage > 0.85; // 85% do scroll
      setIsNearBottom(nearBottom);

      // Carregar mais quando chegar a 85% do scroll
      if (nearBottom && !etapaData.loading && etapaData.hasMore) {
        // Debounce para evitar múltiplas chamadas
        if (loadMoreTimeoutRef.current) {
          clearTimeout(loadMoreTimeoutRef.current);
        }

        loadMoreTimeoutRef.current = setTimeout(() => {
          console.log(`Loading more for stage ${stage.id} - scroll: ${Math.round(scrolledPercentage * 100)}%`);
          onLoadMore();
        }, 100);
      }
    });
  }, [scrollElement, etapaData.hasMore, etapaData.loading, onLoadMore, stage.id]);

  // Configurar scroll listener
  useEffect(() => {
    if (!scrollElement) return;

    const element = scrollElement;
    element.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      element.removeEventListener('scroll', handleScroll);
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, [scrollElement, handleScroll]);

  // Cleanup timeout no unmount
  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, []);

  // Combinar ref do scroll com ref do container para drag & drop
  const combineRefs = useCallback((el: HTMLDivElement | null) => {
    setScrollElement(el);
    containerRef(el);
  }, [containerRef]);

  // Calcular cor do indicador baseado no índice da etapa
  const getStageColor = (index: number) => {
    const colors = ['success', 'info', 'warning', 'danger', 'primary', 'secondary'];
    return colors[index % colors.length];
  };

  // Formatar contador de oportunidades
  const formatOpportunityCount = () => {
    if (!etapaData.initialized) {
      return 'Carregando...';
    }

    const currentCount = etapaData.opportunities.length;
    const totalCount = etapaData.total;
    
    if (etapaData.hasMore) {
      return `${currentCount}+ de ${totalCount} Oportunidades`;
    } else {
      return `${currentCount} Oportunidades`;
    }
  };

  return (
    <div className="kanban-list-items">
      {/* Header da coluna */}
      <div className="card mb-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div className="container">
              <TooltipTitle className="align-items-center mb-1">
                <i className={`ti ti-circle-filled fs-8 text-${getStageColor(stageIndex)} me-2`} />
                {stageIndex + 1}. {stage.nome}
              </TooltipTitle>
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-medium text-default">
                  {formatOpportunityCount()}
                </span>
                
                {/* Botão de refresh */}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary ms-2"
                  onClick={onRefresh}
                  disabled={etapaData.loading}
                  title="Atualizar esta etapa"
                >
                  <i className={`ti ti-refresh ${etapaData.loading ? 'ti-spin' : ''}`} />
                </button>
              </div>

              {/* Indicador de progresso para loading inicial */}
              {etapaData.loading && !etapaData.initialized && (
                <div className="progress mt-2" style={{ height: '2px' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Container com scroll da coluna */}
      <div
        ref={combineRefs}
        data-stage-id={stage.id}
        className="kanban-drag-wrap pt-2 trello-style-column"
        style={{
          maxHeight: '70vh',
          overflowY: 'auto',
          scrollBehavior: 'smooth',
          position: 'relative'
        }}
      >
        {/* Lista de oportunidades */}
        {etapaData.opportunities.map((opportunity, index) => (
          <div 
            key={opportunity.id} 
            data-opportunity-id={opportunity.id}
            data-last-item={index === etapaData.opportunities.length - 1}
            className="opportunity-card"
          >
            {renderOpportunityCard(opportunity)}
          </div>
        ))}

        {/* Loading indicator para load more */}
        {etapaData.loading && etapaData.initialized && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-2 mb-0 text-muted small">
              Carregando mais oportunidades...
            </p>
          </div>
        )}

        {/* Indicador de fim da lista */}
        {etapaData.initialized && !etapaData.hasMore && etapaData.opportunities.length > 0 && (
          <div className="text-center py-3">
            <small className="text-muted">
              <i className="ti ti-check-circle me-1" />
              Todas as {etapaData.total} oportunidades foram carregadas
            </small>
          </div>
        )}

        {/* Estado vazio */}
        {etapaData.initialized && etapaData.opportunities.length === 0 && !etapaData.loading && (
          <div className="text-center py-4">
            <div className="mb-3">
              <i className="ti ti-folder-off fs-36 text-muted" />
            </div>
            <p className="text-muted mb-0">Nenhuma oportunidade nesta etapa</p>
            <small className="text-muted">
              As oportunidades aparecerão aqui quando forem criadas ou movidas para esta etapa
            </small>
          </div>
        )}

        {/* Estado de erro */}
        {etapaData.error && (
          <div className="alert alert-danger mt-2 mb-2">
            <div className="d-flex align-items-center">
              <i className="ti ti-alert-circle me-2" />
              <div className="flex-grow-1">
                <small className="mb-1 d-block">Erro ao carregar:</small>
                <small>{etapaData.error}</small>
              </div>
              <button 
                className="btn btn-sm btn-outline-danger ms-2"
                onClick={onRefresh}
                disabled={etapaData.loading}
              >
                <i className="ti ti-refresh" />
              </button>
            </div>
          </div>
        )}

        {/* Indicador visual de scroll próximo do fim (para debug) */}
        {process.env.NODE_ENV === 'development' && isNearBottom && etapaData.hasMore && (
          <div className="position-absolute bottom-0 end-0 p-2">
            <span className="badge bg-info">
              <i className="ti ti-arrow-down" />
            </span>
          </div>
        )}

        {/* Placeholder para drop - importante para drag & drop */}
        <div className="column-drop-placeholder" style={{ minHeight: '20px' }}></div>
      </div>
    </div>
  );
};

export default KanbanColumn;